import React, { useEffect, useState } from 'react'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { AccountItem } from './AccountItem/AccountItem'
import Styles from './CrossPostAccountsInput.module.scss'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { AlertTriangle } from 'blaze-ui/icons'
import { DEFAULT_OPTIONS, InnerCarousel } from 'components/Carousel/Carousel'
import useEmblaCarousel from 'embla-carousel-react'

export type CrossPostAccount = {
  external_integration_id: number
  output_type: 'text' | 'design'
  provider: ExternalIntegrationEnum
}

interface CrossPostAccountsInputProps {
  value?: CrossPostAccount[]
  onChange?: (newValue: CrossPostAccount[]) => void
  initialProvider?: ExternalIntegrationEnum
  isDesign?: boolean
  errorMessage?: string
}

export const isSupportedProvider = (
  provider: ExternalIntegrationEnum,
): provider is Exclude<ExternalIntegrationEnum, ExternalIntegrationEnum.WordPress | ExternalIntegrationEnum.TikTok> => {
  return provider !== ExternalIntegrationEnum.WordPress && provider !== ExternalIntegrationEnum.TikTok
}

export const CrossPostAccountsInput: React.FC<CrossPostAccountsInputProps> = ({
  value = [],
  onChange = () => {},
  initialProvider,
  isDesign = false,
  errorMessage,
}) => {
  const { data } = useExternalIntegrations()
  const [autoSelectedIntegration, setAutoSelectedIntegration] = useState<number | null>(null)

  const handleSelect = (externalIntegrationId: number) => {
    const isSelected = value.some((account) => account.external_integration_id === externalIntegrationId)
    const integration = data?.external_integrations.find((integration) => integration.id === externalIntegrationId)

    if (!integration || !isSupportedProvider(integration.provider)) {
      return
    }

    if (isSelected) {
      onChange(value.filter((account) => account.external_integration_id !== externalIntegrationId))
    } else {
      const outputType = isDesign ? 'design' : 'text'

      onChange([
        ...value,
        {
          external_integration_id: externalIntegrationId,
          output_type: outputType,
          provider: integration.provider,
        },
      ])
    }
  }

  useEffect(() => {
    if (autoSelectedIntegration) return
    if (initialProvider && data?.external_integrations) {
      const firstIntegration = data.external_integrations.find(
        (integration) => integration.provider === initialProvider && isSupportedProvider(integration.provider),
      )

      if (firstIntegration && !value.some((account) => account.external_integration_id === firstIntegration.id)) {
        setAutoSelectedIntegration(firstIntegration.id)
        onChange([
          ...value,
          {
            external_integration_id: firstIntegration.id,
            output_type: isDesign ? 'design' : 'text',
            provider: firstIntegration.provider,
          },
        ])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProvider, data?.external_integrations, value, autoSelectedIntegration])

  const sortedIntegrations =
    data?.external_integrations.sort((a, b) => {
      const aIsInitialProvider = a.provider === initialProvider
      const bIsInitialProvider = b.provider === initialProvider
      if (aIsInitialProvider && !bIsInitialProvider) return -1
      if (!aIsInitialProvider && bIsInitialProvider) return 1
      return 0
    }) || []

  const embla = useEmblaCarousel({
    ...DEFAULT_OPTIONS,
    dragFree: true,
    align: 'end',
    slidesToScroll: sortedIntegrations.length > 1 ? 'auto' : 1,
  })

  return (
    <div className={Styles.root}>
      {errorMessage && (
        <div className={Styles.errorMessage}>
          <AlertTriangle size={20} />
          {errorMessage}
        </div>
      )}
      <div className={Styles.scrollContainer}>
        <div className={Styles.carouselContainer}>
          <InnerCarousel embla={embla} withControls={false}>
            {sortedIntegrations.map((integration) => {
              const isSelected = value.some((account) => account.external_integration_id === integration.id)
              if (!isSupportedProvider(integration.provider)) {
                return null
              }

              return (
                <AccountItem
                  externalIntegration={integration}
                  isSelected={isSelected}
                  key={integration.id}
                  onSelect={() => handleSelect(integration.id)}
                />
              )
            })}
          </InnerCarousel>
        </div>
      </div>
    </div>
  )
}
